/**
 * Loading placeholders that match the directory's count cards and brand rows.
 * Shown only on the first fetch so a refetch does not flash the list away.
 */

import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { bvColor, bvRadius } from '../brandVendorsTokens';

export const SKELETON_DIRECTORY_ROW_COUNT = 8;

const bone = { bgcolor: bvColor.borderSubtle } as const;

/** Numeral placeholder that keeps the count line from collapsing. */
export function StageCountValueSkeleton({
  width = 32,
  height = 22,
}: {
  width?: number;
  height?: number;
}) {
  return (
    <Skeleton
      variant="rounded"
      animation="wave"
      width={width}
      height={height}
      aria-hidden
      data-testid="stage-count-skeleton"
      sx={{ ...bone, borderRadius: '6px', display: 'inline-block', verticalAlign: 'middle' }}
    />
  );
}

export function BrandDirectoryRowSkeleton({
  grid,
  nameWidth = '62%',
}: {
  grid: string;
  nameWidth?: string;
}) {
  return (
    <Box
      aria-hidden
      data-testid="brand-directory-row-skeleton"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: grid },
        columnGap: '12px',
        rowGap: '8px',
        alignItems: 'center',
        px: '16px',
        py: '11px',
        borderBottom: `1px solid ${bvColor.borderSubtle}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
        <Skeleton
          variant="rounded"
          animation="wave"
          width={40}
          height={40}
          sx={{ ...bone, borderRadius: `${bvRadius.control}px`, flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Skeleton variant="text" animation="wave" width={nameWidth} height={16} sx={bone} />
          <Skeleton variant="text" animation="wave" width="78%" height={14} sx={bone} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: '5px', pr: { lg: '36px' } }}>
        {[0, 1, 2, 3].map((index) => (
          <Skeleton
            key={index}
            variant="rounded"
            animation="wave"
            width={26}
            height={22}
            sx={{ ...bone, borderRadius: `${bvRadius.chip}px` }}
          />
        ))}
      </Box>

      <Skeleton
        variant="rounded"
        animation="wave"
        width={78}
        height={22}
        sx={{ ...bone, borderRadius: `${bvRadius.pill}px` }}
      />

      <Skeleton variant="text" animation="wave" width={72} height={16} sx={bone} />

      <Box sx={{ minWidth: 0 }}>
        <Skeleton variant="text" animation="wave" width="70%" height={16} sx={bone} />
        <Skeleton variant="text" animation="wave" width="52%" height={14} sx={bone} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Skeleton variant="text" animation="wave" width="64%" height={16} sx={bone} />
        <Skeleton variant="text" animation="wave" width="58%" height={14} sx={bone} />
      </Box>

      <Box sx={{ display: { xs: 'none', lg: 'flex' }, justifyContent: 'flex-end' }}>
        <Skeleton variant="rounded" animation="wave" width={16} height={16} sx={bone} />
      </Box>
    </Box>
  );
}

const ROW_NAME_WIDTHS = ['62%', '48%', '71%', '55%', '66%', '44%', '73%', '58%'];

export function BrandDirectoryListSkeleton({
  grid,
  count = SKELETON_DIRECTORY_ROW_COUNT,
}: {
  grid: string;
  count?: number;
}) {
  return (
    <Box aria-busy="true" aria-label="Loading brands">
      {Array.from({ length: count }, (_, index) => (
        <BrandDirectoryRowSkeleton
          key={index}
          grid={grid}
          nameWidth={ROW_NAME_WIDTHS[index % ROW_NAME_WIDTHS.length]}
        />
      ))}
    </Box>
  );
}
